import { useCallback, useEffect, useMemo, useState } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { debounce } from 'es-toolkit';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tz } from '@date-fns/tz';
import { Button, Dialog, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { koKR } from '@mui/x-data-grid/locales';
import { Delete, Download, Search } from '@mui/icons-material';

import { timeZoneAtom } from '@/interfaces/utils';
import { fdaReportsAtom } from '@/interfaces/site';
import { siteMembersAtom } from '@/interfaces/member';
import { deleteReportAtom, getReportUrl } from '@/interfaces/report';
import { download } from '@/components/common/utils';
import { SettingLayout } from '@/components/setting/SettingLayout';
import AssignEndorsementDialog from '@/components/fda/AssignEndorsementDialog';
import SignDialog from '@/components/fda/SignDialog';

import type { ChangeEvent } from 'react';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';
import type { ReportListData } from '@/@types/site';

export const selectedReportAtom = atom<ReportListData>();

export default function ElectronicSignature() {
  const timeZone = useAtomValue(timeZoneAtom);
  const { data: reports, refetch } = useAtomValue(fdaReportsAtom);
  const { data: members } = useAtomValue(siteMembersAtom);
  const { mutate: deleteReport, data: isDeleteSuccess } = useAtomValue(deleteReportAtom);
  const [selectedReport, setSelectedReport] = useAtom(selectedReportAtom);

  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState('');
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (reports === undefined) return;
    setRows(reports);
  }, [reports]);
  useEffect(() => {
    if (isDeleteSuccess === undefined) return;
    if (isDeleteSuccess) {
      refetch();
    } else {
      window.alert(t('Devices.message.reportDeleteFail'));
    }
  }, [isDeleteSuccess, refetch, t]);

  const isKorean = i18n.language.startsWith('ko');
  const locale = isKorean ? koKR.components.MuiDataGrid.defaultProps.localeText : null;
  const timeFormat = isKorean ? 'yyyy.MM.dd a hh:mm' : 'MM/dd/yyyy hh:mm a';
  const getActions = useCallback(
    (params: GridRowParams<ReportListData>) => {
      const { id: reportId, name, status } = params.row;
      const actions: JSX.Element[] = [];

      async function onDownloadClick() {
        const url = await getReportUrl(reportId);
        download(url);
      }
      function onDeleteClick() {
        const ok = window.confirm(`${t('Common.message.confirmDelete')}\n${t('Common.label.name')}: ${name}`);
        if (ok) {
          deleteReport(reportId);
        }
      }

      if (status === 'UNASSIGNED') {
        function onAssignClick() {
          setSelectedReport(params.row);
          setDialog('ASSIGN_ENDORSEMENT');
        }
        const assignButton = <Button onClick={onAssignClick}>{t('FDA.label.assign')}</Button>;
        actions.push(assignButton);
      } else if (status === 'PENDING') {
        function onSignClick() {
          setSelectedReport(params.row);
          setDialog('SIGN');
        }
        const signButton = <Button onClick={onSignClick}>{t('FDA.label.sign')}</Button>;
        actions.push(signButton);
      }
      actions.push(
        <GridActionsCellItem
          icon={<Download fontSize="large" color="success" />}
          label="download"
          onClick={() => onDownloadClick()}
        />,
        <GridActionsCellItem
          icon={<Delete fontSize="large" color="error" />}
          label="delete"
          onClick={() => onDeleteClick()}
        />,
      );

      return actions;
    },
    [deleteReport, setSelectedReport, t],
  );
  const columns = useMemo<GridColDef<ReportListData>[]>(
    () => [
      { field: 'name', headerName: t('Common.label.name'), flex: 2, sortable: false },
      {
        field: 'startedAt',
        headerName: t('FDA.label.startTime'),
        flex: 1,
        valueGetter: (_, row) => row.timeInfo.startedAt,
        valueFormatter: (value: string) => format(value, timeFormat, { in: tz(timeZone) }),
      },
      {
        field: 'endedAt',
        headerName: t('FDA.label.endTime'),
        flex: 1,
        valueGetter: (_, row) => row.timeInfo.endedAt,
        valueFormatter: (value: string) => format(value, timeFormat, { in: tz(timeZone) }),
      },
      {
        field: 'createdAt',
        type: 'dateTime',
        headerName: t('Common.label.createdOn'),
        flex: 1,
        sortable: true,
        valueGetter: (_, row) => row.timeInfo.createdAt,
        valueFormatter: (value: string) => format(value, timeFormat, { in: tz(timeZone) }),
      },
      {
        field: 'creatorId',
        headerName: t('FDA.label.createdBy'),
        flex: 1,
        sortable: false,
        valueFormatter: (value: string) => {
          if (members === undefined) return value;
          const member = members.find((member) => member.id === value);
          return member.info?.name ?? member.email;
        },
      },
      {
        field: 'status',
        headerName: t('Common.label.status'),
        flex: 1,
        sortable: false,
        valueFormatter: (value: string) => t(`FDA.label.${value}`),
      },
      {
        field: 'action',
        type: 'actions',
        headerName: t('Common.label.action'),
        flex: 1,
        sortable: false,
        getActions,
      },
    ],
    [getActions, members, t, timeFormat, timeZone],
  );

  const filterReports = useMemo(
    () =>
      debounce((query: string) => {
        if (query === '') {
          setRows(reports);
        } else {
          setRows(reports.filter((row) => row.name.toLowerCase().includes(query.toLowerCase())));
        }
      }, 500),
    [reports],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    setQuery(value);
    filterReports(value);
  }
  const onDialogClose = useCallback(
    (reason?: string) => {
      setDialog('');

      if (reason === 'updated') {
        refetch();
      }
    },
    [refetch],
  );

  return (
    <SettingLayout>
      <Stack gap={3} sx={{ height: '100%' }}>
        <Typography variant="h5" fontWeight="bold">
          {t('FDA.label.electronicSignature')}
        </Typography>
        <TextField
          size="small"
          placeholder={t('Common.label.name')}
          value={query}
          onChange={onQueryChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: '300px', bgcolor: 'white' }}
        />

        <DataGrid
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          localeText={locale}
          columns={columns}
          rows={rows}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Stack>

      <Dialog
        open={dialog === 'ASSIGN_ENDORSEMENT'}
        onClose={onDialogClose}
        sx={{ '& .MuiPaper-root': { maxWidth: 'unset' } }}
      >
        <AssignEndorsementDialog
          onClose={onDialogClose}
          reportId={selectedReport?.id}
          reportName={selectedReport?.name}
        />
      </Dialog>
      <Dialog open={dialog === 'SIGN'} onClose={onDialogClose} sx={{ '& .MuiPaper-root': { maxWidth: 'unset' } }}>
        <SignDialog onClose={onDialogClose} reportId={selectedReport?.id} reportName={selectedReport?.name} />
      </Dialog>
    </SettingLayout>
  );
}
